import { degToRad, radToDeg } from '@/util/math';

/** Hip spine safe zone bounds from the study API */
export interface HipSpineSafeZone {
    minAnteversion: number;
    maxAnteversion: number;
    minInclination: number;
    maxInclination: number;
}

/** A point on the nomogram representing the current cup position */
export interface NomogramMarker {
    anteversion: number;
    inclination: number;
}

export const CSI_UPPER_BOUND = 245;
export const CSI_LOWER_BOUND = 205;

/**
 * Compute the combined version target anteversion using the Widmer formula.
 *
 *   targetAnteversion = 37.3 - 0.7 × stemAnteversion
 */
export function computeCombinedVersionTarget(stemAnteversion: number): number {
    // 37.3 is the Widmer combined-version target intercept. The canonical value is
    // COMBINED_VERSION_TARGET_INTERCEPT in
    // formus-domain/src/formus_domain/anatomy/hip_spine/nomogram_bounds.py
    // (Hip.Experience.Backend). Keep this literal in step with it.
    return 37.3 - 0.7 * stemAnteversion;
}

/**
 * Determine if a cup position marker falls within a rectangular safe zone.
 */
export function isMarkerInSafeZone(marker: NomogramMarker, zone: HipSpineSafeZone): boolean {
    return (
        marker.anteversion >= zone.minAnteversion &&
        marker.anteversion <= zone.maxAnteversion &&
        marker.inclination >= zone.minInclination &&
        marker.inclination <= zone.maxInclination
    );
}

/**
 * Compute the CSI anteversion at a specific cup inclination for a given CSI bound
 * and pelvic femoral angle.
 *
 *   target (AI) = csiBound - pelvicFemoralAngle
 */
export function computeCSIAnteversion(
    csiBound: number,
    pelvicFemoralAngle: number,
    cupInclination: number,
): number {
    const targetDeg = csiBound - pelvicFemoralAngle;
    const targetRad = degToRad(targetDeg);
    const targetTan = Math.tan(targetRad);
    const cupInclinationRad = degToRad(cupInclination);
    const cupAnteversionRad = Math.atan(targetTan * Math.cos(cupInclinationRad));
    return radToDeg(cupAnteversionRad);
}
